// Role-gating engine for the retailer portal.
//
// Reads the per-retailer 5×18 permissions matrix (GET /portal/role-permissions),
// picks the "current preview role" out of localStorage, and walks every
// element tagged with `data-perm="<key>"` — hiding the ones whose permission
// key is denied for that role. After hiding, walks known grid containers
// and collapses any that lost all visible children, so layouts reflow with
// no blank cells.
//
// Ports the source-of-truth `applyRole()` at
// /var/www/kitchenos/portal-standalone.html:8685 — same body[data-role]
// attribute, same per-permission-key hide loop, same empty-row collapse.
//
// Subscribe via `onRoleChange(handler)` to re-apply when the team page's
// "Preview as" selector flips or a matrix toggle changes. Subscribers run
// AFTER the gate has finished its DOM pass, so page mount code can read
// the final state.

import { api, ApiError } from "@tkos/api-client";

/** Role keys mirror server/role_permissions.js ROLE_KEYS. The 4-role
 *  set replaces the original 5-role spec (Steve 2026-05-21): in kitchen
 *  retail the designer and salesperson are typically the same person,
 *  so the two seats collapse into one combined `designer-sales` row. */
const PORTAL_ROLES = ["owner", "manager", "designer-sales", "fitter"] as const;
export type PortalRole = (typeof PORTAL_ROLES)[number];

const PREVIEW_KEY = "tkos.settings.preview-role";

interface RolePermsResponse {
  ok?: boolean;
  roles?: Record<string, Record<string, boolean>>;
  perms?: { k: string }[];
}

type RoleMatrix = Record<PortalRole, Record<string, boolean>>;

// ── Module-scope state ──────────────────────────────────────────────────────
let matrix: RoleMatrix | null = null;
let permKeys: readonly string[] = [];
let currentRole: PortalRole = readStoredRole();
let lastApplyAt = 0;
let pendingRaf: number | null = null;
let initStarted = false;
const subscribers = new Set<(role: PortalRole) => void>();

// ── Storage ────────────────────────────────────────────────────────────────
function readStoredRole(): PortalRole {
  try {
    const v = localStorage.getItem(PREVIEW_KEY);
    if (v && (PORTAL_ROLES as readonly string[]).includes(v)) return v as PortalRole;
  } catch { /* ignore */ }
  return "owner";
}
function writeStoredRole(r: PortalRole): void {
  try { localStorage.setItem(PREVIEW_KEY, r); } catch { /* ignore */ }
}

// ── Public API ─────────────────────────────────────────────────────────────

/** Begin the engine: fetch the matrix once, then apply. Idempotent.
 *  Subsequent calls are no-ops after the first invocation; pages that mount
 *  after the engine has already initialised get the current state via the
 *  body[data-role] attribute and the `applyRole()` triggered when DOM
 *  changes (callers can also opt in with `applyRoleSoon()`). */
export async function start(authRole?: string): Promise<void> {
  if (initStarted) return;
  initStarted = true;
  // The signed-in user's REAL role is the source of truth — not the stale
  // localStorage "preview" value (which could leave a designer seeing the
  // owner UI). If a valid authenticated role is supplied, use it and persist
  // it so reloads stay consistent. Owners can still preview other roles via
  // the Settings toggle (transient, re-applied here on the next boot).
  if (authRole && (PORTAL_ROLES as readonly string[]).includes(authRole)) {
    currentRole = authRole as PortalRole;
    writeStoredRole(currentRole);
  }
  // Paint the body attribute immediately so any CSS that reacts to
  // [data-role="…"] gets the right value on first paint, even before the
  // matrix is fetched.
  document.body.setAttribute("data-role", currentRole);
  try {
    const resp = await api<RolePermsResponse>("/portal/role-permissions");
    setMatrixFromResponse(resp);
  } catch (err) {
    // The role gate is best-effort: if the matrix endpoint fails, every
    // user sees everything (owner-default). Logging only — don't toast.
    if (err instanceof ApiError) {
      console.warn(`[role-gate] could not load matrix (HTTP ${err.status})`);
    } else {
      console.warn("[role-gate] could not load matrix:", err);
    }
    // Seed a permissive fallback so the engine can still respond to role
    // changes without leaving the matrix null.
    matrix = ownerFallbackMatrix();
  }
  applyRoleNow();
}

/** Currently-applied role. */
export function getCurrentRole(): PortalRole {
  return currentRole;
}

/** Change role + re-apply + notify subscribers. Persists to localStorage. */
export function setCurrentRole(role: PortalRole): void {
  if (!(PORTAL_ROLES as readonly string[]).includes(role)) return;
  if (role === currentRole) {
    // Force a re-apply even on no-op so callers can use this method as
    // "matrix changed, please redraw".
    applyRoleNow();
    return;
  }
  currentRole = role;
  writeStoredRole(role);
  document.body.setAttribute("data-role", role);
  applyRoleNow();
}

/** Receive a fresh matrix payload (server response shape). Triggers a
 *  re-apply so any cells that just changed take effect immediately. */
export function setMatrix(rolesObj: Record<string, Record<string, boolean>>): void {
  matrix = normaliseMatrix(rolesObj);
  if (initStarted) applyRoleNow();
}

/** Internal helper — accepts the full /role-permissions response and
 *  extracts perms + roles. Exposed for the shell's prefetch + the team
 *  tab's reset/toggle paths. */
export function setMatrixFromResponse(resp: RolePermsResponse | null | undefined): void {
  if (!resp || typeof resp !== "object") return;
  if (Array.isArray(resp.perms)) {
    permKeys = resp.perms.map((p) => p.k).filter((k): k is string => typeof k === "string");
  }
  if (resp.roles) {
    matrix = normaliseMatrix(resp.roles);
  }
  if (initStarted) applyRoleNow();
}

/** Subscribe to role changes. Fires once immediately with the current
 *  role so the subscriber paints correctly on attach. Returns unsubscribe. */
export function onRoleChange(handler: (role: PortalRole) => void): () => void {
  subscribers.add(handler);
  try { handler(currentRole); } catch { /* ignore */ }
  return () => { subscribers.delete(handler); };
}

/** Has access? Convenience helper for code paths that need to make a
 *  programmatic decision (e.g. don't fetch data the user can't see). */
export function can(perm: string, role: PortalRole = currentRole): boolean {
  if (role === "owner") return true; // Owner always has access regardless of stored payload.
  if (!matrix) return true;          // Be permissive while the matrix is loading.
  const row = matrix[role];
  if (!row) return true;
  return row[perm] !== false;
}

/** Schedule an applyRole pass on the next animation frame. Pages that
 *  rebuild large chunks of DOM (renderAll, rerender) can call this so the
 *  gate re-checks any newly-rendered [data-perm] elements. Coalesces
 *  multiple calls within the same frame. */
export function applyRoleSoon(): void {
  if (pendingRaf !== null) return;
  pendingRaf = window.requestAnimationFrame(() => {
    pendingRaf = null;
    applyRoleNow();
  });
}

// ── Internals ──────────────────────────────────────────────────────────────

function normaliseMatrix(raw: Record<string, Record<string, boolean>>): RoleMatrix {
  const out = {} as RoleMatrix;
  for (const r of PORTAL_ROLES) {
    const row = raw[r] && typeof raw[r] === "object" ? raw[r] : {};
    const merged: Record<string, boolean> = {};
    for (const k of permKeys) merged[k] = !!row[k];
    out[r] = merged;
  }
  // Owner is always all-true regardless of the stored payload.
  if (permKeys.length > 0) {
    for (const k of permKeys) out.owner[k] = true;
  } else {
    out.owner = {};
  }
  return out;
}

function ownerFallbackMatrix(): RoleMatrix {
  const blank = {} as RoleMatrix;
  for (const r of PORTAL_ROLES) blank[r] = {};
  return blank;
}

function isAllowed(perm: string): boolean {
  return can(perm);
}

/** Containers that should collapse to display:none when all their
 *  [data-perm]-tagged children are hidden. Anything not in this set just
 *  ends up with shorter contents (which is fine for vertical-stack
 *  layouts but bad for explicit grid rows). */
const GRID_CONTAINER_SELECTORS = [
  ".dash-grid",
  ".dash-grid--top",
  ".dash-grid--3",
  ".dash-grid--4",
  ".dash-grid--2",
  ".dash-grid--1124",
  ".dash-grid--feature",
  ".dash-grid--cal-row",
  ".dash-stats",
  ".dash-qa",
] as const;

function applyRoleNow(): void {
  lastApplyAt = Date.now();
  document.body.setAttribute("data-role", currentRole);

  // 1) Walk every [data-perm] element and toggle visibility.
  // We collect them per-perm so we can read the matrix once per key
  // rather than once per element. Owner is permissive by definition.
  const allTagged = document.querySelectorAll<HTMLElement>("[data-perm]");
  allTagged.forEach((el) => {
    const key = el.getAttribute("data-perm");
    if (!key) return;
    const allowed = isAllowed(key);
    if (allowed) {
      // Only clear inline display if WE set it (data-role-hidden flag).
      // This keeps the page's own display state intact for elements that
      // happen to share the data-perm hook.
      if (el.dataset.roleHidden === "1") {
        el.style.removeProperty("display");
        delete el.dataset.roleHidden;
      }
    } else {
      el.style.display = "none";
      el.dataset.roleHidden = "1";
    }
  });

  // 1b) `data-perm-any="key1,key2,…"` — element is visible if ANY of the
  // listed perms is allowed. Used for group wrappers (e.g. megabar
  // dropdowns) that span multiple capability keys: the wrapper hides
  // only when none of its children are accessible.
  const allTaggedAny = document.querySelectorAll<HTMLElement>("[data-perm-any]");
  allTaggedAny.forEach((el) => {
    const raw = el.getAttribute("data-perm-any") || "";
    const keys = raw.split(/[,\s]+/).filter(Boolean);
    if (keys.length === 0) return;
    const anyAllowed = keys.some((k) => isAllowed(k));
    if (anyAllowed) {
      if (el.dataset.roleHidden === "1") {
        el.style.removeProperty("display");
        delete el.dataset.roleHidden;
      }
    } else {
      el.style.display = "none";
      el.dataset.roleHidden = "1";
    }
  });

  // 2) Collapse empty grid containers — same logic as source's setTimeout
  // pass at portal-standalone.html:8702. We run synchronously here; the
  // raf-coalesced version (applyRoleSoon) already gives callers a chance
  // to settle before this fires.
  for (const sel of GRID_CONTAINER_SELECTORS) {
    document.querySelectorAll<HTMLElement>(sel).forEach((grid) => {
      const children = Array.from(grid.children) as HTMLElement[];
      // A container collapses only if it has children AND none of those
      // children are visible. An empty grid with no children is left as-is
      // (it's the page's responsibility to manage that).
      if (children.length === 0) return;
      const anyVisible = children.some((c) => {
        // Direct children may be untagged (e.g. layout wrappers). Treat
        // an untagged child as visible — only role-hidden children count.
        if (c.dataset.roleHidden === "1") return false;
        // Recursively check nested [data-perm] children too — if a
        // wrapper holds only role-hidden tagged children, the wrapper is
        // effectively empty.
        const taggedInside = c.querySelectorAll<HTMLElement>("[data-perm]");
        if (taggedInside.length === 0) return true;
        let anyTaggedVisible = false;
        taggedInside.forEach((t) => {
          if (t.dataset.roleHidden !== "1") anyTaggedVisible = true;
        });
        // If the wrapper has ONLY tagged children and none are visible,
        // hide the wrapper itself.
        if (!anyTaggedVisible && taggedInside.length === c.children.length) {
          c.style.display = "none";
          c.dataset.roleHidden = "1";
          return false;
        }
        return true;
      });
      if (!anyVisible) {
        grid.style.display = "none";
        grid.dataset.roleHidden = "1";
      } else if (grid.dataset.roleHidden === "1") {
        grid.style.removeProperty("display");
        delete grid.dataset.roleHidden;
      }
    });
  }

  // 3) Notify subscribers.
  for (const fn of subscribers) {
    try { fn(currentRole); } catch (err) {
      console.warn("[role-gate] subscriber threw:", err);
    }
  }
}

// Expose lastApplyAt only for test/diagnostic use; not part of the public API.
void lastApplyAt;
